"use client";

import { useMemo, useState } from "react";

import { mapOrganizations } from "@/entities/organization/lib/map-organization";
import { useDebounce } from "@/hooks/useDebounce";
import { useInfiniteOrganizations } from "@/hooks/useOrganizations";
import { useDelayedLoading } from "@/shared/lib/hooks/use-delayed-loading";
import OrganizationDisplay from "@/widgets/organization/organization-display";

/** `serverFallback` — серверный список ссылок на время первой загрузки (SEO). */
export default function OrganizationsClient({
  serverFallback,
}: {
  serverFallback?: React.ReactNode;
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [filterBy, setFilterBy] = useState("all");
  const [sortBy, setSortBy] = useState("name");
  const debouncedSearch = useDebounce(searchQuery, 300);

  const sortDir = useMemo(() => {
    if (sortBy === "name") return "asc";
    if (sortBy === "members") return "desc";
    if (sortBy === "tournaments") return "desc";
    if (sortBy === "subscribers") return "desc";
    return "asc";
  }, [sortBy]);

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteOrganizations({
      ...(debouncedSearch.trim() ? { search: debouncedSearch.trim() } : {}),
      ...(filterBy ? { filterBy } : {}),
      ...(sortBy ? { sortBy } : {}),
      sortDir,
      limit: 24,
    });

  const organizations = useMemo(() => {
    const items = (data?.pages ?? []).flatMap((p) => p.items ?? []);
    return mapOrganizations(items);
  }, [data]);

  // Смена поиска/фильтра/сортировки меняет ключ инфинит-запроса (organizationsPaged
  // включает их все) — без keepPreviousData React Query на миг роняет data в
  // undefined, organizations пустеет, и весь список схлопывался в полноэкранный
  // скелетон, будто страница перезагрузилась. useDelayedLoading (тот же приём,
  // что уже стоит на /tournaments, KAN-306) не даёт скелетону появиться на
  // мгновенных ответах и не мигает при переключении фильтров.
  const showListSkeleton = useDelayedLoading(isLoading && organizations.length === 0);

  if (!data && serverFallback) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-6">{serverFallback}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4">
        <OrganizationDisplay
          organizations={organizations}
          searchInput={searchQuery}
          onSearchChange={setSearchQuery}
          onSearch={setSearchQuery}
          isLoading={showListSkeleton}
          filterBy={filterBy}
          sortBy={sortBy}
          onFilterChange={setFilterBy}
          onSortChange={setSortBy}
          onLoadMore={
            hasNextPage && !isFetchingNextPage ? () => fetchNextPage() : undefined
          }
          hasMore={Boolean(hasNextPage)}
        />
      </div>
    </div>
  );
}
